/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [chat.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 03.03.20, 16:18
 */
import OverlayScrollbars from 'overlayscrollbars';
import moment from 'moment';

import React, {useEffect, useRef} from 'react';
import {UserBoxedLink} from "../../components/navigation/links/nav-headlines";
import {useAuthorization, useFriendData} from "../../selectors";
import {
    asyncFetchChatEntries,
    CHAT_ENTRY_CONSUMED,
    CHAT_ENTRY_RECEIVED,
    EVENT_CHAT_CONSUMED_ACK,
    EVENT_CHAT_DELIVERED,
    EVENT_CHAT_RECEIVED,
    localOpenFriendChat,
    localResetChatEntries
} from "../../actions";
import stompClient, {CHAT_CONSUME_QUEUE, CHAT_DELIVER_QUEUE} from "../../components/messaging/stomp-client";
import {RoundedButton} from "../../components/navigation/buttons/nav-buttons";
import {useIsMobile, useLanguage} from "../../components/configuration/configuration";
import {useDispatch} from "react-redux";
import {DoneAllIcon, fs_smaller, SendIcon} from "../../components/navigation/icons/nav-icons";


const renderChatEntries = (entries, chatId) => {
    const bubbles = entries.map(entry => {

        if (entry.data.chat.id !== chatId) return null;

        const isIncoming = [EVENT_CHAT_DELIVERED, EVENT_CHAT_RECEIVED, EVENT_CHAT_CONSUMED_ACK]
            .includes(entry.event);
        const isConsumed = entry.data.state === CHAT_ENTRY_CONSUMED;
        const isReceived = entry.data.state === CHAT_ENTRY_RECEIVED;

        const incoming = isIncoming ? 'chat-entry-incoming' : 'chat-entry-outgoing';
        const consumed = isConsumed ? 'chat-entry-consumed' : 'chat-entry-delivered';

        // acknowledge receipt and consuming of message
        if (isIncoming && !isConsumed) {
            stompClient.send(CHAT_CONSUME_QUEUE, {
                to: entry.data.from, id: chatId, entryId: entry.data.id
            });
            // TODO !isReceived case - see active-chat.js
        }

        return <div key={entry.data.id} className={`chat-entry ${incoming}`}>
            <div className={`chat-entry-bubble 
            ${isIncoming ? 'incoming corner-left-bottom' : 'outgoing corner-right-top'}`}>
                {entry.data.text}
                {!isIncoming && <DoneAllIcon className={consumed}/>}
                <span className="chat-entry-time text-muted">{moment(entry.data.created).fromNow()}</span>
            </div>
        </div>
    });

    return <div className='bubble-container' ref={(elem) => {
        if (!elem) return;
        let instance = null;
        setTimeout(() => {
            instance = OverlayScrollbars(elem,
                {
                    scrollbars: {visibility: "visible"},
                    callbacks: {
                        onContentSizeChanged: () => {
                            instance && instance.scroll({y: "100%"}, 500, "linear");
                        },
                    }
                });
            instance.scroll({y: "100%"});

        }, 500)
    }}>
        <div className='bubble-container-scroll'>{bubbles}</div>
    </div>
};

const sendMessage = (event, friend, chatId) => {
    event.preventDefault();
    const data = new FormData(event.target);
    event.target.reset();

    if (data.get('message').length > 0) {
        stompClient.send(
            CHAT_DELIVER_QUEUE, {to: friend.username, id: chatId, message: data.get("message")}
        );
    }
};

const Chat = ({className}) => {
    const chatRef = useRef({id: 0});
    const {isAuthorized, principalUsername} = useAuthorization();
    const {chatEntries, friend: friendChat} = useFriendData();
    const Lang = useLanguage();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isAuthorized && friendChat) {
            const {friend, chat} = friendChat;
            chatRef.current = chat;
            friend && dispatch(asyncFetchChatEntries(principalUsername, chat.id, (data) => {
            }));
        }
        return () => {
            // reset current open chat and its corresponding chat entries
            dispatch(localResetChatEntries());
            isMobile && dispatch(localOpenFriendChat(null));
        }
    }, [friendChat]);

    if (!friendChat || friendChat.chat.id !== chatRef.current.id) return null;

    const {friend, chat} = friendChat;
    const homespace = `/${friend.username}/home`;

    // TODO refactor renderChatEntries to a component

    return <div className={`chat-container box-white ${className ? className : ''}`}>
        <UserBoxedLink selected className='box-ocean'
                       to={homespace} user={friend}
                       text={<span className='clr-white w-100 my-1'>{friendChat.status}</span>}
                       online={friendChat.online}/>
        {renderChatEntries(chatEntries, chat.id)}

        <form onSubmit={(event) => sendMessage(event, friend, chat.id)}>
            <div className='chat-input-box'>
                <textarea name='message' placeholder={Lang.chat.you.placeholder}/>
                <RoundedButton type='submit' className='btn-primary ml-1'
                               title={Lang.post.editor.saveTooltip}>
                    <SendIcon size={fs_smaller} aria-hidden="true" className='clr-white'/>
                </RoundedButton>
            </div>
        </form>
    </div>
};

export default Chat;